import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { formatoDinero } from "../lib/money";
import { useAuth } from "../contexts/AuthContext";
// COL.11: el banner vivia aqui; se extrajo a un componente compartido cuando Dashboard y
// Metricas tuvieron que declarar lo mismo. Tres copias del mismo texto son tres sitios
// que se desincronizan (DT.235, DOCTOR_PLAN_LIMITS).
import BannerAlcance from "../components/BannerAlcance";
import { TEXTOS, texto, etiquetaForma, fechaCorta, indicadorAnticipo, ESTADO_CITA_DE_ANTICIPO } from "../lib/anticipos";
import { toast } from "sonner";
import {
  FileSpreadsheet, Download, Lock, AlertTriangle, Ban, Building2, Stethoscope, Inbox,
} from "lucide-react";

// Reportes (Control financiero, FASE 2 entrega 3 — cierra el módulo).
//
// 🔴 ESTA PANTALLA NO SUMA, NO PROMEDIA Y NO DIVIDE. Todas las cifras vienen calculadas
//    del backend, incluidos los totales, los netos y la diferencia entre la suma de los
//    netos y el consolidado. Es el mismo principio de la entrega 1, y es la única forma de
//    que "lo anulado no suma" esté garantizado en un solo sitio: si cada vista tuviera que
//    acordarse de excluir los anulados, la que se olvidara mentiría sobre el mes.
//
// 🔴 Los importes llegan como CADENA y se pintan con `formatoDinero` (lib/money.js), nunca
//    con `formatMXN` (DT.236: redondea los centavos y pasa el valor por `Number`).
//
// 🔴 EL CSV LO GENERA EL BACKEND. No se arma aquí a partir de lo que está en pantalla: el
//    que exporta tiene que ser el que calculó, o los dos pueden desviarse sin que nadie lo
//    note. Esta pantalla sólo pide el archivo y lo entrega al navegador.
//
// 🔴 LA DEMO NO VE ESTE MÓDULO. Dos capas, el patrón de Gastos: la entrada del menú se
//    oculta (AppLayout.js) y aquí hay copy propio por si alguien llega escribiendo la URL.
//    No se deja que falle con el 403 genérico, que no explica nada (DT.71).

const CAMPO =
  "border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-brand-electric";

const PESTANAS = [
  { id: "ingresos", label: "Ingresos" },
  { id: "descuentos", label: "Descuentos" },
  { id: "pendientes", label: "Pendientes de cobro" },
  { id: "gastos", label: "Gastos" },
  { id: "neto", label: "Neto por médico" },
  // CL.4 A2 -- `/api/reportes/anticipos[/csv]` (A1). Mismo patrón que las otras cinco:
  // todo calculado en el backend, CSV generado por el backend sobre la misma función.
  { id: "anticipos", label: TEXTOS.rep_pestana },
];

// El criterio de fecha se dice EN LA PANTALLA, no en un tooltip: un contador va a
// preguntar por qué un abono de este mes sobre un cobro del mes pasado no aparece aquí.
const CRITERIO = {
  ingresos:
    "Todo lo de abajo se agrupa por la fecha del cobro, no por la del pago. Los cobros anulados no suman.",
  descuentos: "Por fecha del cobro. Los cobros anulados no suman.",
  pendientes: "Por fecha del cobro. Los cobros anulados no aparecen.",
  gastos: "Por fecha del gasto. Los gastos anulados no suman.",
  neto: "Por fecha del cobro. Lo anulado no suma, ni en ingresos ni en gastos.",
  anticipos: TEXTOS.rep_criterio,
};

// ═══════════════════════════════════════════════════════════════════════════════
// COL.10 (PASO 5) -- LAS CONSECUENCIAS DEL ALCANCE, PINTADAS.
//
// 🔴 Para un actor acotado, "consolidado" y "gastos de la clínica" llegan en `null` con
//    su nombre en `alcance.no_aplican`. Aquí se pintan CON PALABRAS y nunca como 0.00.
//    Un cero se suma, se grafica y se cree: quien lea "Gastos de clínica: $0.00"
//    concluye que la clínica no gastó nada, no que no le toca verlo. Es dinero, así que
//    un cero ahí es una afirmación financiera FALSA.
//
// 🔴 Y tampoco se deja en blanco ni con el guion de `formatoDinero(null)`: un hueco se
//    lee como "no cargó" o como un bug, que es otra cosa distinta otra vez.
//
// 🔴 "No hay datos" (`sin_datos`) y "esto no te corresponde" (`no_aplican`) son DOS
//    señales separadas del backend y se pintan DISTINTO. Pueden ser ciertas a la vez y
//    significan cosas distintas; colapsarlas deja a la persona sin saber cuál de las dos
//    está viendo. Por eso el banner de alcance se pinta TAMBIÉN sobre el estado vacío.
// ═══════════════════════════════════════════════════════════════════════════════

function NoAplica() {
  return (
    <span
      className="inline-block text-sm font-medium text-slate-500 italic"
      data-testid="no-aplica"
      title="Tu acceso está acotado a ciertos médicos, así que esta cifra no se calcula para ti."
    >
      No aplica a tu alcance
    </span>
  );
}

/** Un importe que PUEDE no aplicar. `noAplica` viene de `alcance.no_aplican`, no de que
 *  el valor sea nulo: así "no te corresponde" no se confunde con "no hay dato". */
function Importe({ valor, noAplica }) {
  if (noAplica) return <NoAplica />;
  return <>{formatoDinero(valor)}</>;
}

function Tarjeta({ titulo, valor, ayuda, tono = "normal" }) {
  const tonos = {
    normal: "bg-white border-slate-200",
    fuerte: "bg-slate-900 border-slate-900 text-white",
    aviso: "bg-amber-50 border-amber-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${tonos[tono]}`}>
      <div className={`text-xs font-medium ${tono === "fuerte" ? "text-slate-300" : "text-slate-500"}`}>
        {titulo}
      </div>
      <div className="text-2xl font-semibold mt-1 tabular-nums">{valor}</div>
      {ayuda ? (
        <div className={`text-xs mt-1 ${tono === "fuerte" ? "text-slate-400" : "text-slate-500"}`}>
          {ayuda}
        </div>
      ) : null}
    </div>
  );
}

function Tabla({ columnas, filas, vacio }) {
  if (!filas.length) {
    return <div className="text-sm text-slate-500 py-6 text-center">{vacio}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
            {columnas.map((c) => (
              <th key={c.k} className={`py-2 px-2 font-medium ${c.num ? "text-right" : ""}`}>
                {c.t}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0">
              {columnas.map((c) => (
                <td
                  key={c.k}
                  className={`py-2 px-2 ${c.num ? "text-right tabular-nums" : ""} ${
                    c.fuerte ? "font-medium text-slate-900" : "text-slate-700"
                  }`}
                >
                  {c.render ? c.render(f) : f[c.k]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SinDatos({ desde, hasta }) {
  // 🔴 Un periodo sin movimientos NO se pinta como una fila de ceros. Un cero que
  //    significa "no hubo" y un cero que significa "sumó cero" son cosas distintas, y sólo
  //    el backend sabe cuál es — por eso la señal es su `sin_datos`, no `filas.length`.
  return (
    <div className="py-12 text-center">
      <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-3" />
      <div className="text-slate-700 font-medium">No hubo movimientos en este periodo</div>
      <div className="text-sm text-slate-500 mt-1">
        Del {desde} al {hasta}. No es que las cifras sumen cero: no hay nada registrado en esas fechas.
      </div>
    </div>
  );
}

export default function Reportes() {
  const { user } = useAuth();
  const esDemo = !!user?.is_demo_session;

  const [pestana, setPestana] = useState("ingresos");
  const [datos, setDatos] = useState({});
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null); // {detail, code}
  const [descargando, setDescargando] = useState(false);
  const [fDesde, setFDesde] = useState("");
  const [fHasta, setFHasta] = useState("");

  // Fecha de negocio de la clínica, tomada de la primera respuesta.
  // 🔴 Nunca `new Date()` del navegador: la fecha de negocio la decide la zona de la
  //    clínica y el navegador puede estar en otra. Mismo motivo por el que el backend no
  //    usa CURRENT_DATE.
  const hoyRef = useRef(null);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    if (fDesde) p.set("desde", fDesde);
    if (fHasta) p.set("hasta", fHasta);
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [fDesde, fHasta]);

  const cargar = useCallback(
    async (cual) => {
      setCargando(true);
      setError(null);
      try {
        const { data } = await api.get(`/reportes/${cual}${qs()}`);
        setDatos((d) => ({ ...d, [cual]: data }));
        if (!hoyRef.current) hoyRef.current = data.hasta;
        if (!fDesde && !fHasta) {
          setFDesde(data.desde);
          setFHasta(data.hasta);
        }
      } catch (e) {
        // Se ramifica por `code`, nunca por el texto (DT.60). Y no hay catch mudo: si el
        // código no es uno de los esperados, igual se muestra lo que dijo el backend.
        const code = e?.response?.data?.code;
        const detail = e?.response?.data?.detail;
        if (code === "RANGO_DEMASIADO_LARGO" || code === "RANGO_INVALIDO") {
          setError({ code, detail: formatApiError(detail) });
        } else if (code === "NO_ACTIVE_CLINIC") {
          setError({ code, detail: formatApiError(detail) });
        } else if (code === "DEMO_SIN_REPORTES") {
          // Ramifica por `code`, no por el status: un 403 puede venir de otras cosas.
          // El backend lo rechaza aunque esta pantalla ya no llame en modo demo — la
          // allowlist de `_DEMO_ALLOWED_MUTATIONS` sólo mira verbos mutantes y estos son
          // todos GET, así que sin ese guardia no habría ninguna barrera de servidor.
          setError({ code, detail: formatApiError(detail) });
        } else {
          setError({
            code: code || "ERROR",
            detail: detail ? formatApiError(detail) : "No se pudo cargar el reporte.",
          });
        }
      } finally {
        // El estado de carga se limpia SIEMPRE, también si la promesa reventó antes de
        // llegar al catch por un fallo síncrono.
        setCargando(false);
      }
    },
    [qs, fDesde, fHasta]
  );

  useEffect(() => {
    if (esDemo) {
      setCargando(false);
      return;
    }
    cargar(pestana);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pestana, esDemo]);

  const aplicarRango = () => {
    setDatos({});
    cargar(pestana);
  };

  const descargarCsv = async () => {
    setDescargando(true);
    try {
      const resp = await api.get(`/reportes/${pestana}/csv${qs()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pestana}-${fDesde}-a-${fHasta}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          ? formatApiError(e.response.data.detail)
          : "No se pudo generar el archivo."
      );
    } finally {
      setDescargando(false);
    }
  };

  if (esDemo) {
    return (
      <div className="p-6 lg:p-8 max-w-3xl mx-auto w-full">
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <Lock className="w-8 h-8 text-slate-400 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-slate-900">Reportes</h1>
          <p className="text-sm text-slate-600 mt-2">
            La demo pública no maneja dinero, así que no hay ingresos, gastos ni cobros que
            reportar. En una clínica real, aquí se ve lo cobrado y lo gastado del periodo, el
            detalle de descuentos, lo que queda por cobrar y el neto de cada médico.
          </p>
        </div>
      </div>
    );
  }

  const d = datos[pestana];

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-6xl mx-auto w-full">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-brand-electric" /> Reportes
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Control financiero de la clínica.</p>
        </div>
        <button
          onClick={descargarCsv}
          disabled={descargando || cargando || !d || d.sin_datos}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
          data-testid="reportes-csv"
        >
          <Download className="w-4 h-4" />
          {descargando ? "Generando…" : "Descargar CSV"}
        </button>
      </div>

      <div className="flex items-end gap-2 flex-wrap">
        <label className="text-xs text-slate-500">
          Desde
          <input
            type="date"
            className={`block ${CAMPO}`}
            value={fDesde}
            max={hoyRef.current || undefined}
            onChange={(e) => setFDesde(e.target.value)}
            data-testid="reportes-desde"
          />
        </label>
        <label className="text-xs text-slate-500">
          Hasta
          <input
            type="date"
            className={`block ${CAMPO}`}
            value={fHasta}
            max={hoyRef.current || undefined}
            onChange={(e) => setFHasta(e.target.value)}
            data-testid="reportes-hasta"
          />
        </label>
        <button
          onClick={aplicarRango}
          className="rounded-lg bg-slate-900 text-white px-3 py-1.5 text-sm hover:bg-slate-800"
          data-testid="reportes-aplicar"
        >
          Aplicar
        </button>
      </div>

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {PESTANAS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPestana(p.id)}
            className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${
              pestana === p.id
                ? "border-brand-electric text-slate-900 font-medium"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`reportes-tab-${p.id}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="text-xs text-slate-500 flex items-start gap-1.5" data-testid="reportes-criterio">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>{CRITERIO[pestana]}</span>
      </div>

      {/* El alcance se declara SIEMPRE que haya respuesta, incluso cuando no hubo
          movimientos: son dos señales distintas y las dos hacen falta para leer la
          pantalla. */}
      {!cargando && !error && d ? <BannerAlcance alcance={d.alcance} /> : null}

      {cargando ? (
        <div className="py-12 text-center text-slate-500 text-sm">Cargando…</div>
      ) : error ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {error.detail}
        </div>
      ) : !d ? null : d.sin_datos ? (
        <SinDatos desde={d.desde} hasta={d.hasta} />
      ) : (
        <>
          {pestana === "ingresos" && <VistaIngresos d={d} />}
          {pestana === "descuentos" && <VistaDescuentos d={d} />}
          {pestana === "pendientes" && <VistaPendientes d={d} />}
          {pestana === "gastos" && <VistaGastos d={d} />}
          {pestana === "neto" && <VistaNeto d={d} />}
          {pestana === "anticipos" && <VistaAnticipos d={d} />}
        </>
      )}
    </div>
  );
}

function VistaIngresos({ d }) {
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tarjeta
          titulo="Cobros emitidos"
          valor={formatoDinero(d.emitido)}
          ayuda={`${d.cobros} ${d.cobros === 1 ? "cobro" : "cobros"} en el periodo`}
        />
        <Tarjeta titulo="Cobrado" valor={formatoDinero(d.cobrado)} ayuda="De esos cobros" tono="fuerte" />
        <Tarjeta titulo="Por cobrar" valor={formatoDinero(d.por_cobrar)} ayuda="Saldo de esos cobros" />
        <Tarjeta
          titulo="Entró en el periodo"
          valor={formatoDinero(d.entro_en_periodo)}
          ayuda="Pagos recibidos entre esas fechas, aunque el cobro sea de otro mes"
          tono="aviso"
        />
      </div>

      <p className="text-xs text-slate-500">
        <strong className="text-slate-700">Cobrado</strong> es lo efectivamente pagado de los cobros
        emitidos en el periodo. <strong className="text-slate-700">Entró en el periodo</strong> es el
        dinero que llegó a caja entre esas fechas, sin importar de qué mes sea el cobro. Son dos
        cifras distintas a propósito y no tienen por qué coincidir.
      </p>

      {Number(d.anulado.conteo) > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 flex items-center gap-2">
          <Ban className="w-4 h-4 shrink-0" />
          <span>
            Además hay {d.anulado.conteo} {d.anulado.conteo === 1 ? "cobro anulado" : "cobros anulados"} por{" "}
            {formatoDinero(d.anulado.importe)}. <strong>No suman</strong> en ninguna cifra de arriba.
          </span>
        </div>
      )}

      <section>
        <h2 className="text-sm font-semibold text-slate-900 mb-2">Por médico</h2>
        <Tabla
          columnas={[
            { k: "doctor", t: "Médico", fuerte: true, render: (f) => f.doctor || "Sin médico (de la clínica)" },
            { k: "emitido", t: "Cobros emitidos", num: true, render: (f) => formatoDinero(f.emitido) },
            { k: "cobrado", t: "Cobrado", num: true, render: (f) => formatoDinero(f.cobrado) },
            { k: "cobros", t: "N.º cobros", num: true },
          ]}
          filas={d.por_doctor}
          vacio="Ningún cobro en el periodo."
        />
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-900 mb-1">Por servicio</h2>
        <p className="text-xs text-slate-500 mb-2">
          Sobre los cobros emitidos. Un pago corresponde al cobro completo, no a un concepto, así que
          esta tabla no se puede desglosar por lo cobrado.
        </p>
        <Tabla
          columnas={[
            { k: "concepto", t: "Concepto", fuerte: true },
            { k: "importe", t: "Importe", num: true, render: (f) => formatoDinero(f.importe) },
            { k: "lineas", t: "Líneas", num: true },
            {
              k: "del_catalogo",
              t: "Origen",
              render: (f) => (
                <span className="text-xs text-slate-500">
                  {f.del_catalogo ? "Del catálogo" : "Capturado a mano"}
                </span>
              ),
            },
          ]}
          filas={d.por_servicio}
          vacio="Ningún concepto cobrado en el periodo."
        />
      </section>
    </div>
  );
}

function VistaDescuentos({ d }) {
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <Tarjeta titulo="Total descontado" valor={formatoDinero(d.total)} tono="fuerte" />
        <Tarjeta
          titulo="Líneas con descuento"
          valor={d.conteo}
          ayuda="Cada una con su motivo, abajo"
        />
      </div>
      <p className="text-xs text-slate-500">
        Esto es lo que explica por qué el ingreso no coincide con los precios del catálogo.
      </p>
      <Tabla
        columnas={[
          { k: "fecha", t: "Fecha" },
          { k: "doctor", t: "Médico", render: (f) => f.doctor || "—" },
          { k: "concepto", t: "Concepto", fuerte: true },
          { k: "precio_unitario", t: "Precio", num: true, render: (f) => formatoDinero(f.precio_unitario) },
          { k: "descuento", t: "Descuento", num: true, render: (f) => formatoDinero(f.descuento) },
          { k: "motivo", t: "Motivo" },
        ]}
        filas={d.filas}
        vacio="No se aplicó ningún descuento en el periodo."
      />
    </div>
  );
}

function VistaPendientes({ d }) {
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <Tarjeta titulo="Saldo total" valor={formatoDinero(d.saldo_total)} tono="fuerte" />
        <Tarjeta titulo="Cobros con saldo" valor={d.conteo} ayuda="Pendientes o parciales" />
      </div>
      <Tabla
        columnas={[
          { k: "fecha", t: "Fecha" },
          { k: "paciente", t: "Paciente", fuerte: true, render: (f) => f.paciente || "—" },
          { k: "doctor", t: "Médico", render: (f) => f.doctor || "—" },
          { k: "total", t: "Total", num: true, render: (f) => formatoDinero(f.total) },
          { k: "cobrado", t: "Cobrado", num: true, render: (f) => formatoDinero(f.cobrado) },
          { k: "saldo", t: "Saldo", num: true, render: (f) => formatoDinero(f.saldo) },
          {
            k: "estado",
            t: "Estado",
            render: (f) => (
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  f.estado === "parcial" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
                }`}
              >
                {f.estado === "parcial" ? "Parcial" : "Pendiente"}
              </span>
            ),
          },
        ]}
        filas={d.filas}
        vacio="No queda nada por cobrar en el periodo."
      />
    </div>
  );
}

function VistaGastos({ d }) {
  // `no_aplican` trae los NOMBRES de los campos que el backend dejó en `null` por el
  // alcance. Se pregunta por el nombre, no por "el valor es nulo": así no se confunde
  // "no te corresponde" con "no hay dato".
  const naClinica = (d.alcance?.no_aplican || []).includes("de_clinica");
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <Tarjeta titulo="Total de gastos" valor={formatoDinero(d.total)} tono="fuerte" />
        <Tarjeta
          titulo="De médicos"
          valor={formatoDinero(d.de_medico)}
          ayuda="Se descuentan del resultado de cada médico"
        />
        <Tarjeta
          titulo="De la clínica"
          valor={<Importe valor={d.de_clinica} noAplica={naClinica} />}
          ayuda={naClinica
            ? "Son los gastos sin médico asignado, y tu acceso está acotado a médicos concretos."
            : "Gastos generales, no imputados a ningún médico"}
        />
      </div>

      {Number(d.anulado.conteo) > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 flex items-center gap-2">
          <Ban className="w-4 h-4 shrink-0" />
          <span>
            Además hay {d.anulado.conteo} {d.anulado.conteo === 1 ? "gasto anulado" : "gastos anulados"} por{" "}
            {formatoDinero(d.anulado.importe)}. <strong>No suman.</strong>
          </span>
        </div>
      )}

      <Tabla
        columnas={[
          { k: "categoria", t: "Categoría", fuerte: true, render: (f) => f.categoria },
          { k: "total", t: "Total", num: true, render: (f) => formatoDinero(f.total) },
          { k: "de_medico", t: "De médicos", num: true, render: (f) => formatoDinero(f.de_medico) },
          // Fila a fila con el MISMO criterio que el total: dejar 0.00 aquí contradiría
          // el "no aplica" de arriba dentro de la misma pantalla.
          { k: "de_clinica", t: "De la clínica", num: true, render: (f) => <Importe valor={f.de_clinica} noAplica={naClinica} /> },
          { k: "conteo", t: "N.º gastos", num: true },
        ]}
        filas={d.filas}
        vacio="No hay gastos registrados en el periodo."
      />
    </div>
  );
}

function VistaNeto({ d }) {
  const na = d.alcance?.no_aplican || [];
  const naIng = na.includes("ingresos_clinica");
  const naGas = na.includes("gastos_clinica");
  const naCons = na.includes("consolidado");
  return (
    <div className="space-y-5">
      <Tabla
        columnas={[
          { k: "doctor", t: "Médico", fuerte: true, render: (f) => f.doctor || f.doctor_id },
          { k: "cobrado", t: "Cobrado", num: true, render: (f) => formatoDinero(f.cobrado) },
          { k: "gastos", t: "Sus gastos", num: true, render: (f) => formatoDinero(f.gastos) },
          {
            k: "neto",
            t: "Neto",
            num: true,
            render: (f) => (
              <span className={Number(f.neto) < 0 ? "text-rose-700 font-medium" : "font-medium"}>
                {formatoDinero(f.neto)}
              </span>
            ),
          },
        ]}
        filas={d.por_medico}
        vacio="Ningún médico con movimientos en el periodo."
      />

      {/* 🔴 LAS TRES CIFRAS POR SEPARADO Y LA EXPLICACIÓN EN LA PANTALLA, no en un tooltip:
          el primer cliente va a pensar que las cuentas no cuadran. */}
      <div className="grid gap-3 sm:grid-cols-3">
        <Tarjeta
          titulo="Suma de netos por médico"
          valor={formatoDinero(d.suma_netos_medicos)}
          ayuda="Sólo lo imputado a médicos"
        />
        <Tarjeta
          titulo="De la clínica"
          valor={<Importe valor={d.ingresos_clinica} noAplica={naIng} />}
          ayuda={
            // Los dos van juntos para un actor acotado, pero se pregunta por los dos:
            // con uno solo, el texto de ayuda pintaria el guion de `formatoDinero(null)`.
            naIng || naGas
              ? "Los ingresos y gastos sin médico son de la clínica entera, fuera de tu alcance."
              : `Ingresos sin médico, menos ${formatoDinero(d.gastos_clinica)} de gastos generales`
          }
        />
        <Tarjeta
          titulo="Consolidado"
          valor={<Importe valor={d.consolidado} noAplica={naCons} />}
          tono="fuerte"
          ayuda={naCons ? "Es el resultado de toda la clínica" : "Toda la clínica"}
        />
      </div>

      {naCons ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 space-y-2"
             data-testid="neto-consolidado-no-aplica">
          <div className="font-medium flex items-center gap-2">
            <Building2 className="w-4 h-4" /> Por qué faltan tres cifras
          </div>
          <p>
            Tu acceso está acotado a médicos concretos, y el consolidado, los ingresos y los
            gastos de la clínica incluyen lo que no está imputado a ningún médico —renta, luz,
            insumos generales—. Eso no te corresponde, así que <strong>no se calcula</strong>:
            si saliera en cero parecería que la clínica no tuvo ni ingresos ni gastos, y sería
            falso. Lo que sí ves arriba es cierto y completo dentro de tu alcance.
          </p>
        </div>
      ) : (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 space-y-2">
        <div className="font-medium text-slate-900 flex items-center gap-2">
          <Building2 className="w-4 h-4" /> Por qué la suma de los netos no da el consolidado
        </div>
        <p>
          No es un error de las cifras. Los gastos que no se imputan a ningún médico —renta, luz,
          insumos generales— son de la clínica, y los ingresos sin médico asignado también. Ese
          dinero no aparece en el neto de nadie, pero sí en el consolidado.
        </p>
        <p className="text-slate-600">
          <Stethoscope className="w-3.5 h-3.5 inline mr-1" />
          El neto de cada médico es lo que se <strong>cobró</strong> de sus cobros menos los gastos
          registrados a su nombre. No incluye la parte que corresponde a la clínica.
        </p>
      </div>
      )}
    </div>
  );
}

// CL.4 A2 -- pestaña «Anticipos». 🔴 NADA SE SUMA AQUÍ: recibidos, aplicados, retenidos,
// devueltos y el saldo por aplicar AL CORTE llegan calculados (el saldo, reconstruido por
// el backend a la fecha `hasta`, no con el estado de hoy). Importes como cadena.
// Corrección 1: el mismo estado con la MISMA palabra que en la Agenda («Anticipo recibido»).
function EstadoAnticipo({ estado }) {
  const ind = indicadorAnticipo(ESTADO_CITA_DE_ANTICIPO[estado]);
  return (
    <span className={`text-xs ${estado === "por_aplicar" ? "text-emerald-700" : "text-slate-600"}`}>
      {ind ? ind.etiqueta : estado}
    </span>
  );
}

export function VistaAnticipos({ d }) {
  const corte = fechaCorta(d.corte);
  const columnas = [
    { k: "fecha_recibido", t: TEXTOS.rep_col_fecha, render: (f) => fechaCorta(f.fecha_recibido) },
    { k: "paciente", t: TEXTOS.rep_col_paciente, fuerte: true, render: (f) => f.paciente || "—" },
    { k: "doctor", t: TEXTOS.rep_col_medico, render: (f) => f.doctor || "—" },
    { k: "monto", t: TEXTOS.rep_col_monto, num: true, render: (f) => formatoDinero(f.monto) },
    { k: "forma_pago", t: TEXTOS.rep_col_forma, render: (f) => etiquetaForma(f.forma_pago) },
    { k: "referencia", t: TEXTOS.rep_col_referencia, render: (f) => f.referencia || "—" },
    { k: "cita_fecha", t: TEXTOS.rep_col_cita, render: (f) => (f.cita_fecha ? fechaCorta(f.cita_fecha) : "—") },
    { k: "estado", t: TEXTOS.rep_col_estado, render: (f) => <EstadoAnticipo estado={f.estado} /> },
  ];
  return (
    <div className="space-y-5" data-testid="reportes-anticipos">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Tarjeta titulo={TEXTOS.rep_recibidos} valor={formatoDinero(d.entro_anticipos)} ayuda={TEXTOS.rep_recibidos_ayuda} />
        <Tarjeta titulo={TEXTOS.rep_aplicados} valor={formatoDinero(d.aplicado_en_periodo)} ayuda={TEXTOS.rep_aplicados_ayuda} />
        <Tarjeta titulo={TEXTOS.rep_retenidos} valor={formatoDinero(d.retenido_en_periodo)} ayuda={TEXTOS.rep_retenidos_ayuda} />
        <Tarjeta titulo={TEXTOS.rep_devueltos} valor={formatoDinero(d.salio_devoluciones)} ayuda={TEXTOS.rep_devueltos_ayuda} />
        <Tarjeta
          titulo={texto("rep_saldo", { corte })}
          valor={<span data-testid="anticipos-saldo">{formatoDinero(d.saldo_por_aplicar_al_corte)}</span>}
          ayuda={TEXTOS.rep_saldo_ayuda}
          tono="fuerte"
        />
      </div>

      {Number(d.anulado.conteo) > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 flex items-center gap-2">
          <Ban className="w-4 h-4 shrink-0" />
          <span>
            {texto("rep_anulados", {
              n: d.anulado.conteo,
              cosa: d.anulado.conteo === 1 ? TEXTOS.rep_anulado_uno : TEXTOS.rep_anulado_varios,
              importe: formatoDinero(d.anulado.importe),
            })}
          </span>
        </div>
      )}

      <section>
        <h2 className="text-sm font-semibold text-slate-900 mb-2">{texto("rep_tabla_por_aplicar", { corte })}</h2>
        <Tabla columnas={columnas} filas={d.por_aplicar} vacio={TEXTOS.rep_vacio_por_aplicar} />
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-900 mb-2">{TEXTOS.rep_tabla_recibidos}</h2>
        <Tabla columnas={columnas} filas={d.recibidos} vacio={TEXTOS.rep_vacio_recibidos} />
      </section>
    </div>
  );
}
